// ...existing code...

// 1. 取得元素座標（相容多裝置）
// getElementPosition(element, canvasMode)
export const getElementPosition = (element, canvasMode) => {
    if (!element) return { x: 0, y: 0 };
    let mode = canvasMode;
    if (canvasMode === "auto") {
        // 監聽 resize 事件，自動 re-render
        if (typeof window !== "undefined") {
            // 只需註冊一次
            if (!window.__canvas_auto_resize_listener) {
                window.__canvas_auto_resize_listener = true;
                window.addEventListener("resize", () => {
                    // 觸發全域事件，讓 React 組件 re-render
                    window.dispatchEvent(new Event("canvasAutoResize"));
                });
            }
        }
        const w = window.innerWidth;
        if (w >= 1000) mode = "desktop";
        else if (w >= 600) mode = "tablet";
        else mode = "mobile";
    }
    if (typeof element.x === "object" && typeof element.y === "object") {
        return {
            x: element.x[mode] ?? element.x.desktop ?? 0,
            y: element.y[mode] ?? element.y.desktop ?? 0,
        };
    }
    return { x: element.x ?? 0, y: element.y ?? 0 };
};

// 1b. 取得元素尺寸（相容多裝置 responsive 格式與舊版單一數字）
// 新格式：element.width = { desktop: 300, tablet: 200, mobile: 150 }
// 舊格式：element.width = 300（直接數字）
export const getElementSize = (element, canvasMode) => {
    if (!element) return { width: 0, height: 0 };
    let mode = canvasMode;
    if (canvasMode === "auto") {
        const w = typeof window !== "undefined" ? window.innerWidth : 1200;
        if (w >= 1000) mode = "desktop";
        else if (w >= 600) mode = "tablet";
        else mode = "mobile";
    }
    // 優先使用 object 格式（各裝置獨立），其次 style，最後 fallback 數字
    const wSrc = (typeof element?.width === "object" && element?.width !== null)
        ? element.width
        : (typeof element?.style?.width === "object" && element?.style?.width !== null)
            ? element.style.width
            : null
    const hSrc = (typeof element?.height === "object" && element?.height !== null)
        ? element.height
        : (typeof element?.style?.height === "object" && element?.style?.height !== null)
            ? element.style.height
            : null

    const width = wSrc
        ? Number(wSrc[mode] ?? wSrc.desktop ?? 0)
        : Number(element?.style?.width ?? element?.width ?? 0)
    const height = hSrc
        ? Number(hSrc[mode] ?? hSrc.desktop ?? 0)
        : Number(element?.style?.height ?? element?.height ?? 0)
    return { width, height };
};
export const centerAndHalf = (el, canvasMode) => {
    const { x, y } = getElementPosition(el, canvasMode);
    const { width: w, height: h } = getElementSize(el, canvasMode);
    return {
        cx: x + w / 2,
        cy: y + h / 2,
        hw: Math.max(w / 2, 0.0001),
        hh: Math.max(h / 2, 0.0001),
    };
};

// 3. 求矩形邊界交點
export const edgePointToward = (cx, cy, hw, hh, tx, ty) => {
    const dx = tx - cx;
    const dy = ty - cy;
    const sx = Math.abs(dx) / hw;
    const sy = Math.abs(dy) / hh;
    const t = Math.max(sx, sy, 1);
    return { x: cx + dx / t, y: cy + dy / t };
};

// 4. 算兩元素的連線外框與端點
export const computeLineGeom = (fromEl, toEl, padding = 0, canvasMode) => {
    const { cx: sx, cy: sy, hw: shw, hh: shh } = centerAndHalf(fromEl, canvasMode);
    const { cx: ex, cy: ey, hw: ehw, hh: ehh } = centerAndHalf(toEl, canvasMode);

    const p1 = edgePointToward(sx, sy, shw, shh, ex, ey);
    const p2 = edgePointToward(ex, ey, ehw, ehh, sx, sy);

    const left = Math.min(p1.x, p2.x) - padding;
    const top = Math.min(p1.y, p2.y) - padding;
    const width = Math.max(Math.abs(p2.x - p1.x), 1) + padding * 2;
    const height = Math.max(Math.abs(p2.y - p1.y), 1) + padding * 2;

    return {
        bbox: { left, top, width, height },
        rel: {
            p1: { x: p1.x - left, y: p1.y - top },
            p2: { x: p2.x - left, y: p2.y - top },
        },
    };
};

// 5. 允許些微誤差，避免 0.0001 抖動
const EPS = 0.5;
export const nearly = (a, b, eps = EPS) => Math.abs(a - b) <= eps;

// 6. 取得優先級顏色
export const getPriorityColor = (priority) => {
    switch (priority) {
        case "high":
            return "text-red-600 bg-red-50"
        case "medium":
            return "text-yellow-600 bg-yellow-50"
        case "low":
            return "text-green-600 bg-green-50"
        default:
            return "text-gray-600 bg-gray-50"
    }
}

// 7. 取得優先級文字
export const getPriorityText = (priority) => {
    switch (priority) {
        case "high":
            return "高"
        case "medium":
            return "中"
        case "low":
            return "低"
        default:
            return "中"
    }
}

// 8. 取得線條樣式
export const getLineStyle = (line, selectedElement) => {
    const baseStyle = {
        stroke: selectedElement?.id === line?.id ? "#000" : "#000",
        strokeWidth: selectedElement?.id === line.id ? "3" : "2",
    }
    if (line?.style?.lineStyle === "dashed") {
        baseStyle.strokeDasharray = "5,5"
    }
    return baseStyle
}

// 9. 取得 flow 連線外框與端點
export const getEdgeCenterGeom = (fromEl, toEl, canvasMode, padding = 0) => {
    const { x: fx, y: fy } = getElementPosition(fromEl, canvasMode);
    const { x: tx, y: ty } = getElementPosition(toEl, canvasMode);
    const w1 = Number(fromEl?.style?.width ?? fromEl?.width ?? 0);
    const h1 = Number(fromEl?.style?.height ?? fromEl?.height ?? 0);
    const p1 = { x: fx + w1, y: fy + h1 / 2 };

    const w2 = Number(toEl?.style?.width ?? toEl?.width ?? 0);
    const h2 = Number(toEl?.style?.height ?? toEl?.height ?? 0);
    const p2 = { x: tx, y: ty + h2 / 2 };

    const left = Math.min(p1.x, p2.x) - padding;
    const top = Math.min(p1.y, p2.y) - padding;
    const width = Math.max(Math.abs(p2.x - p1.x), 1) + padding * 2;
    const height = Math.max(Math.abs(p2.y - p1.y), 1) + padding * 2;

    return {
        bbox: { left, top, width, height },
        rel: {
            p1: { x: p1.x - left, y: p1.y - top },
            p2: { x: p2.x - left, y: p2.y - top },
        },
    };
};

// 10. 格式化日期時間（含秒）
export const formatDateTimeWithSeconds = (date) => {
    const pad = (num) => String(num).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}
function getStepNumber(data, selection_option_id) {
    // // 找出符合 selection_option_id 的那筆資料
    // const found = data.find(item => item.selection_option_id === selection_option_id);
    // if (!found) return null;

    // // 取出 language_culture_value，例如 "5個步驟"
    // const value =
    //     found.selection_option_classify_structure_type_data?.[0]?.name_language_data?.[0]
    //         ?.language_culture_value || "";

    // // 用正則取出整數部分
    const numMatch = selection_option_id.match(/\d+/);
    return numMatch ? parseInt(numMatch[0], 10) : null;
}
// 11. 取得流程5w處理節點數量
export const getStepCountFromValues = (values) => {
    for (const fivew of values?.five_w_data || []) {
        for (const col of fivew?.form_column_data || []) {
            if (col?.code === "處理步驟節點數") {
                const stepNumber = getStepNumber(col?.form_column_selection_option_data, col?.value?.value)
                return stepNumber ?? 2; // 直接回傳 else_setting
            }
        }
    }
    return undefined; // 沒找到則回傳 undefined
}
// 計算節點呈現位置與連線
export const buildNodes = (stepCount, fivewData) => {
    // Input: stepCount (Number), fivewData (Array)
    // Output: Array<Node>
    const nodes = [];
    const now = Date.now();

    // Start Node
    nodes.push({
        component_definitions_id: 136,
        id: `start_node-${now}`,
        type: "start_node",
        x: 100,
        y: 100,
        width: 200,
        height: 150,
        title: "開始",
        description: "",
    });

    // Process Nodes
    for (let i = 0; i < stepCount; i++) {
        const nodeId = `process_node-${now}-${i}`;

        // 找對應的 five_w_data (可能不足 stepCount，要判斷)
        const fivewItem = fivewData[i] || {};
        const five_w_sub_id = fivewItem?.five_w_sub_id || null;

        nodes.push({
            component_definitions_id: 137,
            id: nodeId,
            type: "process_node",
            x: 100 + (i + 1) * 220,
            y: 100,
            width: 200,
            height: 150,
            title: `步驟${i + 1}`,
            description: "",
            five_w_sub_id,         // 🔑 帶入流程 5W 設定資料
            five_w_data: fivewItem // 🔑 可以整筆存起來（如果後續要用到其他欄位）
        });
    }

    // End Node
    nodes.push({
        component_definitions_id: 139,
        id: `end_node-${now}`,
        type: "end_node",
        x: 100 + (stepCount + 1) * 220,
        y: 100,
        width: 200,
        height: 150,
        title: "結束",
        description: "",
    });

    return nodes;
}

// 依節點關係產生連接線
export const buildLines = (nodes) => {
    // Input: nodes (Array<Node>)
    // Output: Array<Line>
    const lines = [];
    const startNode = nodes.find(n => n.type === "start_node");
    const processNodes = nodes.filter(n => n.type === "process_node");
    const endNode = nodes.find(n => n.type === "end_node");

    if (startNode && processNodes.length > 0) {
        lines.push({
            id: `line-${startNode.id}-${processNodes[0].id}`,
            type: "flow_line",
            fromId: startNode.id,
            toId: processNodes[0].id,
        });
    }

    for (let i = 0; i < processNodes.length - 1; i++) {
        lines.push({
            id: `line-${processNodes[i].id}-${processNodes[i + 1].id}`,
            type: "flow_line",
            fromId: processNodes[i].id,
            toId: processNodes[i + 1].id,
        });
    }

    if (processNodes.length > 0 && endNode) {
        lines.push({
            id: `line-${processNodes[processNodes.length - 1].id}-${endNode.id}`,
            type: "flow_line",
            fromId: processNodes[processNodes.length - 1].id,
            toId: endNode.id,
        });
    }

    return lines;
}

// 呼叫 API 建立每個節點對應的 form_column
export async function createFormColumns(nodes, formColumnController) {
    // Input: nodes (Array<Node>)
    // Output: nodes (Array<Node>) with form_form_column_data
    for (let node of nodes) {
        let postData = [
            {
                "form_column_component_definition_data": [
                    { "component_definition_id": node?.component_definitions_id }
                ]
            }
        ];
        try {
            const res = await formColumnController.postData(postData);
            node.form_form_column_data = res?.values?.form_column_data;
        } catch (e) {
            node.form_form_column_data = [];
        }
    }
    return nodes;
}
export const get5wFormColumns = (element) => {

    if (!element) return [];

    try {
        let form_column_form_column_data_first = element?.form_form_column_data?.[0];
        let form_column_source_data_first = form_column_form_column_data_first?.form_column_source_data?.[0];
        let document_source_data_first = form_column_source_data_first?.document_source_data?.[0];
        let document_source_data_source_data_first = document_source_data_first?.document_source_data?.[0];
        let document_form_data_first = document_source_data_source_data_first?.document_form_data?.[0];
        let document_id = document_source_data_source_data_first?.document_id
        let new_form_form_column_data = (document_form_data_first?.form_form_column_data || []).map((value) => {
            return {
                ...value,
                // document_id:document_id
            }
        })

        return new_form_form_column_data || [];
    } catch (err) {
        console.error("get5wFormColumns error:", err);
        return [];
    }
}
export const getProcessType = () => {
    const pageRoute = window.location.pathname || ""

    if (pageRoute.includes("multi-track-mix")) {
        return "MultiTrackMix" // 多軌複合
    } else if (pageRoute.includes("multi-track")) {
        return "multiTrack" // 多軌循序
    } else {
        return "process" // 單軌
    }
}
export const getStepTypeLimit = (processType) => {
    const limits = {
        process: {
            start_node: 1,
            process_node: -1, // 無限制
            end_node: 1,
            decision_node: 0
        },
        multiTrack: {
            start_node: 1,
            process_node: -1,
            end_node: 1,
            decision_node: 1
        },
        MultiTrackMix: {
            start_node: -1,
            process_node: -1,
            end_node: 1,
            decision_node: -1
        }
    }

    return limits[processType] || {
        start_node: 1,
        process_node: -1, // 無限制
        end_node: 1,
        decision_node: 0
    }
}
export const getCanvasStepCount = (pages = []) => {
    const stepCount = {
        start_node: 0,
        process_node: 0,
        end_node: 0,
        decision_node: 0,
    }

    const traverse = (elements = []) => {
        elements.forEach((el) => {
            if (el.type && stepCount.hasOwnProperty(el.type)) {
                stepCount[el.type] += 1
            }
            if (el.elements && Array.isArray(el.elements)) {
                traverse(el.elements)
            }
        })
    }

    pages.forEach((page) => {
        if (page.elements && Array.isArray(page.elements)) {
            traverse(page.elements)
        }
    })

    return stepCount
}

export const checkStepLimit = (canvasStepCount, stepTypeLimit) => {
    const result = {}

    for (let key in canvasStepCount) {
        const count = canvasStepCount[key] || 0
        const limit = stepTypeLimit[key]

        if (limit === -1) {
            result[key] = 0 // 無限制
        } else {
            result[key] = count > limit ? 1 : 0
        }
    }

    return result
}

/**
 * 將 document_response_data 中的回饋內容，
 * 根據 form_column_id 對應填入 form_form_column_data 裡。
 *
 * @param {Array} form_form_column_data - 表單欄位設定資料（例如欄位代號、元件定義）
 * @param {Array} document_response_data - 文件的回饋內容資料
 * @returns {Array} - 填入回饋內容後的新 form_form_column_data 陣列
 */
export function fillFormResponseData(form_form_column_data, document_response_data) {
    // --- 資料驗證 ---
    if (!Array.isArray(form_form_column_data) || !Array.isArray(document_response_data)) {
        // console.error("❌ 傳入的資料格式不正確，必須為陣列");
        return form_form_column_data;
    }

    // --- 取出第一筆回饋內容 ---
    // 假設目前僅需要第一筆回饋資料 (通常只有一筆)  q
    const firstResponse = document_response_data.find((_) => true)
    if (!firstResponse || !Array.isArray(firstResponse?.response_form_value_data)) {
        // console.warn("⚠️ 找不到有效的 response_form_value_data");
        return form_form_column_data;
    }

    // 建立一個 Map 用來存放欄位與對應的回饋值
    // key: form_column_id
    // value: { value, form_value_id }
    const responseMap = new Map();

    // --- 解析 response_form_value_data ---
    // 每一筆 response_form_value_data 對應使用者填的一個欄位
    firstResponse?.response_form_value_data.forEach((res) => {
        // 有些欄位會有多個 form_column_form_value_data（同一組欄位設定）
        if (Array.isArray(res?.form_column_form_value_data)) {
            res?.form_column_form_value_data.forEach((fcv) => {
                if (fcv?.form_column_id) {
                    // 將欄位 ID 與對應的值記錄下來
                    responseMap.set(fcv?.form_column_id, {
                        // res?.value 是物件，通常結構為 { value: ... }
                        value: res?.value ?? null,
                        form_value_id: res?.form_value_id ?? null,
                    });
                }
            });
        }
    });


    // --- 將回饋內容填入對應欄位 ---
    const updatedData = form_form_column_data.map((col) => {
        // 根據 form_column_id 找到對應的回饋資料
        const match = responseMap.get(col?.form_column_id);

        if (match) {
            // 若有找到對應的回饋值，將 value 與 form_value_id 填入欄位物件中
            return {
                ...col,
                value: match.value,              // 實際填入的使用者輸入值
                form_value_id: match.form_value_id, // 回饋資料的唯一識別 ID
            };
        }

        // 若找不到對應欄位，保持原樣
        return col;
    });

    // --- 回傳結果 ---
    return updatedData;
}
// 12. 將 responses 轉換為表格資料
export const transformResponses = (responses, columns = []) => {
    return (responses || []).map(resp => {
        // 初始化欄位為空字串，確保每筆都有這兩個欄位
        const row = {};
        columns.forEach(c => row[c] = '');

        (resp?.response_form_value_data || []).forEach(item => {
            // 取第一筆 form_column_form_value_data 的 code 作為欄位名稱
            const meta = (item.form_column_form_value_data && item.form_column_form_value_data[0]) || null;
            const code = meta?.code;
            const val = item?.value?.value ?? '';

            if (code ) {
                row[code] = val;
            }
        });

        return row;
    });
}






